/**
 * DOS518 — Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * AstutoImport.tsx
 * Pannello di importazione dati da ASTUTO (CFRS Sicilia).
 * Supporta 4 modalità: API REST, File, Clipboard, Demo.
 *
 * Step: config → input → preview → done
 */

import React, { useState, useRef } from 'react';
import {
  AstutoMode,
  AstutoConfig,
  AstutoIntervento,
  ImportOptions,
  DEFAULT_IMPORT_OPTIONS,
  loadAstutoConfig,
  saveAstutoConfig,
  getAdapter,
  applyAstutoImport,
} from '../../integrations/astuto';

type Step = 'config' | 'input' | 'preview' | 'done';

interface AstutoImportProps {
  onClose?: () => void;
}

const MODALITA: { value: AstutoMode; label: string; desc: string; icon: string }[] = [
  { value: 'rest',      icon: '🌐', label: 'API REST',   desc: 'Connessione diretta ad ASTUTO (richiede URL e rete)' },
  { value: 'file',      icon: '📂', label: 'File',        desc: 'Importa un file .xml, .csv o .json esportato da ASTUTO' },
  { value: 'clipboard', icon: '📋', label: 'Clipboard',   desc: 'Incolla direttamente il testo copiato da ASTUTO' },
  { value: 'disabled',  icon: '🧪', label: 'Demo',        desc: 'Dati campione per test e configurazione iniziale' },
];

export const AstutoImport: React.FC<AstutoImportProps> = ({ onClose }) => {
  const [step, setStep]       = useState<Step>('config');
  const [config, setConfig]   = useState<AstutoConfig>(loadAstutoConfig);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore]   = useState('');
  const [dati, setDati]       = useState<AstutoIntervento | null>(null);
  const [opzioni, setOpzioni] = useState<ImportOptions>(DEFAULT_IMPORT_OPTIONS);
  const [riepilogo, setRiepilogo] = useState('');
  const [clipTesto, setClipTesto] = useState('');

  const fileRef = useRef<HTMLInputElement>(null);

  // ── Step CONFIG ────────────────────────────────────────────────────────────

  const salvaConfig = () => {
    saveAstutoConfig(config);
    setStep('input');
  };

  // ── Step INPUT ─────────────────────────────────────────────────────────────

  const eseguiFetch = async (input?: File | string) => {
    setLoading(true);
    setErrore('');
    try {
      const adapter = getAdapter(config.mode);
      const result  = await adapter.fetch(input);
      if (result.ok) {
        setDati(result.data);
        setStep('preview');
      } else {
        setErrore((result as { ok: false; error: string }).error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await eseguiFetch(file);
    // Reset il valore per permettere di ricaricare lo stesso file
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClipboardPaste = async () => {
    if (!clipTesto.trim()) {
      // Prova a leggere dalla clipboard del browser
      try {
        const text = await navigator.clipboard.readText();
        setClipTesto(text);
        await eseguiFetch(text);
      } catch {
        setErrore('Impossibile leggere la clipboard. Incolla il testo manualmente nel campo.');
      }
      return;
    }
    await eseguiFetch(clipTesto);
  };

  // ── Step PREVIEW / IMPORT ──────────────────────────────────────────────────

  const handleImport = () => {
    if (!dati) return;
    const ris = applyAstutoImport(dati, opzioni);
    setRiepilogo(ris || 'Nessun dato da importare in base alle opzioni selezionate.');
    setStep('done');
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={S.overlay}>
      <div style={S.modal}>

        {/* Header */}
        <div style={S.header}>
          <div>
            <div style={S.headerTitle}>🌲 Import da ASTUTO</div>
            <div style={S.headerSub}>
              {step === 'config'  && 'Configura la modalità di connessione'}
              {step === 'input'   && `Modalità: ${MODALITA.find(m => m.value === config.mode)?.label}`}
              {step === 'preview' && 'Anteprima dati — scegli cosa importare'}
              {step === 'done'    && 'Importazione completata'}
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} style={S.closeBtn}>✕</button>
          )}
        </div>

        <div style={S.body}>

          {/* ── STEP: CONFIG ── */}
          {step === 'config' && (
            <>
              <p style={S.desc}>
                Seleziona come DOS518 deve connettersi ad ASTUTO per recuperare
                i dati del personale e dei mezzi dell'intervento.
              </p>

              <div style={S.modiGrid}>
                {MODALITA.map((m) => (
                  <div
                    key={m.value}
                    style={{
                      ...S.modCard,
                      ...(config.mode === m.value ? S.modCardActive : {}),
                    }}
                    onClick={() => setConfig((c) => ({ ...c, mode: m.value }))}
                  >
                    <div style={S.modIcon}>{m.icon}</div>
                    <div style={S.modLabel}>{m.label}</div>
                    <div style={S.modDesc}>{m.desc}</div>
                  </div>
                ))}
              </div>

              {/* Campi aggiuntivi per REST */}
              {config.mode === 'rest' && (
                <div style={S.restFields}>
                  <div style={S.fieldGrp}>
                    <label style={S.fieldLabel}>URL base ASTUTO *</label>
                    <input
                      style={S.fieldInput}
                      placeholder="es. https://astuto.cfrs.it/api/v1"
                      value={config.restBaseUrl ?? ''}
                      onChange={(e) => setConfig((c) => ({ ...c, restBaseUrl: e.target.value }))}
                    />
                  </div>
                  <div style={S.fieldGrp}>
                    <label style={S.fieldLabel}>API Key / Token (se richiesto)</label>
                    <input
                      style={S.fieldInput}
                      type="password"
                      placeholder="Bearer token o chiave API"
                      value={config.restApiKey ?? ''}
                      onChange={(e) => setConfig((c) => ({ ...c, restApiKey: e.target.value }))}
                    />
                  </div>
                  <div style={S.restNote}>
                    ℹ️ L'endpoint verrà aggiornato non appena CFRS pubblicherà la documentazione API ufficiale.
                    Attualmente: <code style={{ color: '#93c5fd' }}>GET {'{baseUrl}'}/interventi/corrente</code>
                  </div>
                </div>
              )}

              <div style={S.footer}>
                {onClose && (
                  <button onClick={onClose} style={S.btnSecondary}>Annulla</button>
                )}
                <button onClick={salvaConfig} style={S.btnPrimary}>
                  Avanti →
                </button>
              </div>
            </>
          )}

          {/* ── STEP: INPUT ── */}
          {step === 'input' && (
            <>
              {errore && <div style={S.erroreBox}>{errore}</div>}

              {/* REST */}
              {config.mode === 'rest' && (
                <div style={S.inputSection}>
                  <p style={S.desc}>
                    DOS518 chiamerà l'API ASTUTO e recupererà automaticamente
                    il personale e i mezzi assegnati all'intervento corrente.
                  </p>
                  <button
                    style={S.btnPrimary}
                    onClick={() => eseguiFetch()}
                    disabled={loading}
                  >
                    {loading ? '⏳ Connessione ad ASTUTO...' : '🌐 Recupera dati da ASTUTO'}
                  </button>
                </div>
              )}

              {/* FILE */}
              {config.mode === 'file' && (
                <div style={S.inputSection}>
                  <p style={S.desc}>
                    Dalla SOUP, esporta l'intervento da ASTUTO in formato
                    <strong> XML</strong>, <strong>CSV</strong> o <strong>JSON</strong>
                    {' '}e carica il file qui.
                  </p>
                  <button
                    style={S.btnPrimary}
                    onClick={() => fileRef.current?.click()}
                    disabled={loading}
                  >
                    {loading ? '⏳ Lettura file...' : '📂 Seleziona file ASTUTO'}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xml,.csv,.json"
                    style={{ display: 'none' }}
                    onChange={handleFile}
                  />
                  <div style={S.fileNote}>
                    Formati supportati: <code>.xml</code> · <code>.csv</code> · <code>.json</code>
                  </div>
                </div>
              )}

              {/* CLIPBOARD */}
              {config.mode === 'clipboard' && (
                <div style={S.inputSection}>
                  <p style={S.desc}>
                    Copia i dati dell'intervento da ASTUTO (JSON, XML o CSV)
                    e incollali nel campo sottostante oppure usa il pulsante
                    "Incolla dalla clipboard".
                  </p>
                  <textarea
                    style={S.clipTextarea}
                    rows={8}
                    placeholder={'Incolla qui il testo copiato da ASTUTO...\n\nEsempi accettati:\n• JSON: {"personale": [...]}\n• XML: <Intervento>...</Intervento>\n• CSV: [PERSONALE]\\nNOME;COGNOME;...'}
                    value={clipTesto}
                    onChange={(e) => setClipTesto(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button
                      style={{ ...S.btnSecondary, flex: 1 }}
                      onClick={async () => {
                        try {
                          const t = await navigator.clipboard.readText();
                          setClipTesto(t);
                        } catch {
                          setErrore('Permesso clipboard negato. Incolla manualmente.');
                        }
                      }}
                    >
                      📋 Incolla dalla clipboard
                    </button>
                    <button
                      style={{ ...S.btnPrimary, flex: 1 }}
                      onClick={handleClipboardPaste}
                      disabled={loading || !clipTesto.trim()}
                    >
                      {loading ? '⏳ Analisi...' : '🔍 Analizza testo'}
                    </button>
                  </div>
                </div>
              )}

              {/* DEMO */}
              {config.mode === 'disabled' && (
                <div style={S.inputSection}>
                  <div style={S.demoNotice}>
                    🧪 Modalità demo — verranno caricati dati fittizi per testare
                    l'importazione. I record saranno etichettati "(DEMO)" nel diario operativo.
                  </div>
                  <button
                    style={S.btnPrimary}
                    onClick={() => eseguiFetch()}
                    disabled={loading}
                  >
                    {loading ? '⏳ Caricamento...' : '🧪 Carica dati demo'}
                  </button>
                </div>
              )}

              <div style={S.footer}>
                <button onClick={() => { setErrore(''); setStep('config'); }} style={S.btnSecondary}>
                  ← Configurazione
                </button>
              </div>
            </>
          )}

          {/* ── STEP: PREVIEW ── */}
          {step === 'preview' && dati && (
            <>
              <div style={S.previewBox}>
                <div style={S.previewTitle}>✅ Dati letti correttamente</div>

                <div style={S.previewRow}>
                  {dati.codice   && <span>📋 <strong>Codice:</strong> {dati.codice}</span>}
                  {dati.comune   && <span>📍 <strong>Comune:</strong> {dati.comune}{dati.localita ? ` — ${dati.localita}` : ''}</span>}
                  {dati.latitudine && <span>🌐 <strong>Coord:</strong> {dati.latitudine}, {dati.longitudine}</span>}
                </div>
                <div style={S.previewRow}>
                  <span>👥 {dati.personale.length} operatori</span>
                  <span>🚒 {dati.mezziTerrestri.length} mezzi terrestri</span>
                  <span>🚁 {dati.mezziAerei.length} mezzi aerei</span>
                </div>

                {/* Lista personale */}
                {dati.personale.length > 0 && (
                  <div style={S.previewLista}>
                    <div style={S.previewListaTitle}>👥 Personale</div>
                    {dati.personale.map((p, i) => (
                      <div key={i} style={S.previewItem}>
                        <span style={{ fontWeight: 600 }}>{p.nome} {p.cognome}</span>
                        {p.qualifica && <span style={S.previewBadge}>{p.qualifica}</span>}
                        {p.ente && <span style={S.previewEnte}>{p.ente}</span>}
                        {p.telefono && <span style={S.previewSub}>📞 {p.telefono}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Opzioni */}
              <div style={S.opzioniSection}>
                <div style={S.opzioniTitle}>⚙️ Cosa importare in DOS518</div>
                {([
                  { key: 'importaPersonale',      label: `👥 Personale (${dati.personale.length})` },
                  { key: 'importaMezziTerrestri', label: `🚒 Mezzi terrestri (${dati.mezziTerrestri.length})` },
                  { key: 'importaMezziAerei',     label: `🚁 Mezzi aerei (${dati.mezziAerei.length})` },
                  { key: 'importaCoordinate',     label: '📍 Coordinate punto intervento' },
                  { key: 'importaCOAU',           label: '📋 Dati COAU (comune, codice)' },
                ] as { key: keyof ImportOptions; label: string }[]).map(({ key, label }) => (
                  <label key={key} style={S.opzioneLabel}>
                    <input
                      type="checkbox"
                      checked={opzioni[key] as boolean}
                      onChange={(e) => setOpzioni((p) => ({ ...p, [key]: e.target.checked }))}
                      style={{ width: 16, height: 16, accentColor: '#2563eb' }}
                    />
                    {label}
                  </label>
                ))}

                <label style={{ ...S.opzioneLabel, color: '#f97316', marginTop: 8, fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={opzioni.svuotaEsistenti}
                    onChange={(e) => setOpzioni((p) => ({ ...p, svuotaEsistenti: e.target.checked }))}
                    style={{ width: 16, height: 16, accentColor: '#ef4444' }}
                  />
                  ⚠️ Svuota dati esistenti prima di importare
                </label>
              </div>

              <div style={S.footer}>
                <button onClick={() => { setDati(null); setStep('input'); }} style={S.btnSecondary}>
                  ← Indietro
                </button>
                <button onClick={handleImport} style={S.btnImport}>
                  ✅ Importa in DOS518
                </button>
              </div>
            </>
          )}

          {/* ── STEP: DONE ── */}
          {step === 'done' && (
            <div style={S.doneSection}>
              <div style={{ fontSize: 56 }}>✅</div>
              <div style={S.doneTitle}>Importazione completata</div>
              <div style={S.doneDesc}>{riepilogo}</div>
              <p style={{ color: '#64748b', fontSize: 13, marginTop: 8 }}>
                Verifica personale e mezzi nelle rispettive sezioni.
                L'import è registrato nel Diario Operativo.
              </p>
              {onClose ? (
                <button onClick={onClose} style={S.btnPrimary}>
                  Chiudi
                </button>
              ) : (
                <button onClick={() => { setStep('config'); setDati(null); setRiepilogo(''); }} style={S.btnPrimary}>
                  Nuovo import
                </button>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ─── Stili ────────────────────────────────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9998, padding: 16,
  },
  modal: {
    background: '#0d1520',
    border: '1px solid #1e3a5f',
    borderRadius: 12,
    width: '100%', maxWidth: 680,
    maxHeight: '92vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '18px 24px 14px',
    borderBottom: '1px solid #1e3a5f',
    background: 'linear-gradient(135deg, #0d1520 0%, #1e3a5f 100%)',
    flexShrink: 0,
  },
  headerTitle: { fontSize: 17, fontWeight: 700, color: '#60a5fa' },
  headerSub:   { fontSize: 12, color: '#64748b', marginTop: 3 },
  closeBtn:    { background: 'none', border: 'none', color: '#64748b', fontSize: 20, cursor: 'pointer' },
  body: { overflowY: 'auto', flex: 1, padding: '20px 24px' },

  // Config step
  desc: { color: '#94a3b8', fontSize: 13, marginBottom: 16, lineHeight: 1.5 },
  modiGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    gap: 10, marginBottom: 20,
  },
  modCard: {
    padding: '14px 16px', borderRadius: 8,
    border: '2px solid #1e3a5f',
    background: '#0f1a2e', cursor: 'pointer',
    transition: 'all 0.15s',
  },
  modCardActive: {
    borderColor: '#2563eb',
    background: 'rgba(37,99,235,0.12)',
  },
  modIcon:  { fontSize: 22, marginBottom: 4 },
  modLabel: { fontWeight: 700, color: '#e2e8f0', fontSize: 14 },
  modDesc:  { color: '#64748b', fontSize: 11, marginTop: 3, lineHeight: 1.4 },

  // REST fields
  restFields: {
    background: '#0f1a2e',
    border: '1px solid #1e3a5f',
    borderRadius: 8, padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: 10,
    marginBottom: 16,
  },
  fieldGrp:   { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldLabel: { color: '#94a3b8', fontSize: 12, fontWeight: 600 },
  fieldInput: {
    padding: '8px 12px',
    background: '#111827', border: '1px solid #1e3a5f',
    borderRadius: 6, color: '#e2e8f0', fontSize: 13, outline: 'none',
  },
  restNote: {
    color: '#64748b', fontSize: 11, lineHeight: 1.5,
    borderTop: '1px solid #1e3a5f', paddingTop: 10, marginTop: 4,
  },

  // Input step
  inputSection: {
    display: 'flex', flexDirection: 'column', gap: 12,
    padding: '16px', background: '#0f1a2e',
    borderRadius: 8, border: '1px solid #1e3a5f',
    marginBottom: 16,
  },
  fileNote: { color: '#475569', fontSize: 12, textAlign: 'center' },
  clipTextarea: {
    padding: '10px 12px',
    background: '#111827', border: '1px solid #1e3a5f',
    borderRadius: 6, color: '#e2e8f0', fontSize: 12,
    resize: 'vertical', outline: 'none', fontFamily: 'monospace',
  },
  demoNotice: {
    background: 'rgba(251,191,36,0.08)',
    border: '1px solid rgba(251,191,36,0.3)',
    borderRadius: 6, padding: '10px 14px',
    color: '#fbbf24', fontSize: 13, lineHeight: 1.5,
  },

  // Preview step
  previewBox: {
    background: '#0f1a2e',
    border: '1px solid #166534',
    borderRadius: 8, padding: '14px 16px',
    marginBottom: 16,
  },
  previewTitle: { color: '#22c55e', fontWeight: 700, fontSize: 15, marginBottom: 10 },
  previewRow: {
    display: 'flex', flexWrap: 'wrap', gap: 12,
    color: '#e2e8f0', fontSize: 13, marginBottom: 8,
  },
  previewLista: { marginTop: 12 },
  previewListaTitle: { color: '#60a5fa', fontWeight: 600, fontSize: 12, marginBottom: 6 },
  previewItem: {
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    padding: '5px 0', borderBottom: '1px solid #1e2535',
    color: '#e2e8f0', fontSize: 13,
  },
  previewBadge: {
    background: 'rgba(37,99,235,0.2)', color: '#93c5fd',
    padding: '1px 7px', borderRadius: 4, fontSize: 11,
  },
  previewEnte: { color: '#64748b', fontSize: 11 },
  previewSub:  { color: '#475569', fontSize: 11 },

  // Opzioni
  opzioniSection: {
    background: '#0f1a2e',
    border: '1px solid #1e3a5f',
    borderRadius: 8, padding: '14px 16px',
    marginBottom: 16,
  },
  opzioniTitle: { color: '#60a5fa', fontWeight: 700, fontSize: 14, marginBottom: 12 },
  opzioneLabel: {
    display: 'flex', alignItems: 'center', gap: 10,
    color: '#e2e8f0', marginBottom: 8, cursor: 'pointer', fontSize: 13,
  },

  // Error
  erroreBox: {
    background: '#1a0808',
    border: '1px solid #7f1d1d',
    borderRadius: 6, padding: '10px 14px',
    color: '#f87171', fontSize: 13, marginBottom: 12,
  },

  // Done
  doneSection: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    textAlign: 'center', padding: '24px 0', gap: 10,
  },
  doneTitle: { color: '#22c55e', fontWeight: 700, fontSize: 20 },
  doneDesc:  { color: '#94a3b8', fontSize: 14 },

  // Footer
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: 10,
    paddingTop: 8,
  },

  // Buttons
  btnPrimary: {
    padding: '10px 22px', borderRadius: 7, border: 'none',
    background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
    color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
  },
  btnSecondary: {
    padding: '10px 18px', borderRadius: 7,
    border: '1px solid #1e3a5f',
    background: 'transparent', color: '#64748b',
    fontSize: 13, cursor: 'pointer',
  },
  btnImport: {
    padding: '10px 22px', borderRadius: 7, border: 'none',
    background: 'linear-gradient(135deg, #166534, #22c55e)',
    color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(34,197,94,0.3)',
  },
};
